import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from "react-toastify";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserById } from '../../../redux/slice/getUserByIdSlice';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { updateCandidateStatus } from '../../../redux/slice/updateCandidateStatusSlice';
import { Button, Modal } from "react-bootstrap";
import CrossIcon from "../../../assets/img/file-cut-icon.png";
import FinalCandidateStatus from './FinalCandidateStatus';
import { getAllUploadedFile } from "../../../redux/slice/filesUploadSlice";
import { useDocumentUpload } from "../../../hooks/useDocumentUpload";
import { useForm } from 'react-hook-form';
import { intervalToDuration, parseISO, format } from "date-fns";

export default function CandidateDetailsForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { invitedBy } = location.state || "";
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue
  } = useForm();
  const { User, status } = useSelector(state => state?.User);
  const InitialPermissionData = useSelector((state) => state.persistedReducer.forms.savedPermissions);
  const { email, isApprover } = InitialPermissionData ?? [];
  const { allFiles } = useSelector((state) => state.filesUpload);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [formData, setFormData] = useState();
  const [fileTypeObject, setFileTypeObject] = useState({});
  const canView = useHasPermission("Candidate Details-view");
  const canViewCandidateStatus = useHasPermission("Candidate Status-view");
  const canViewFinalCandidateStatus = useHasPermission("Final Update Candidate Status-view");
  const { handleViewFile } = useDocumentUpload({ candidateId: id });
  const { additionalDetails, addresses, educationDetails, employmentHistory, personalDetail, professionalReferences } = User || {};

  useEffect(() => {
    if (canView) {
      dispatch(fetchUserById(id));
    }
  }, [dispatch, canView]);

  useEffect(() => {
    setValue("dateAttach", getCurrentDate());
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(getAllUploadedFile(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    setFileTypeObject(allFiles.reduce((acc, curr) => {
      const cleanKey = curr.fileType
      acc[cleanKey] = true;

      return acc;
    }, {}))
  }, [allFiles])

  const statusMapping = {
    "Verified": 1,
    "Rejected": 2,
    "Sent back for revision": 3,
    "Sent for Approval to Admin": 4
  };

  const ALLOWED_TRANSITIONS = {
    // Default/First time
    "SUBMITTED": ["Verified", "Rejected", "Sent back for revision"],

    // On verified
    "VERIFIED": ["Rejected", "Sent for Approval to Admin"],

    // On rejected
    "REJECTED": ["Sent back for revision", "Sent for Approval to Admin"],

    // On sent back for revision
    "SENT_BACK_FOR_REVISION": ["Verified", "Rejected"],
  };

  const toastConfig = {
    "Verified": {
      msg: "Candidate has been verified successfully!",
      options: { className: "toast-success-custom" }
    },
    "Rejected": {
      msg: "Candidate has been rejected!",
      options: { className: "toast-error-custom" }
    },
    "Sent back for revision": {
      msg: "Candidate details sent back for revision",
      options: { className: "toast-info-custom" }
    },
    "Sent for Approval to Admin": {
      msg: "Candidate approved successfully!",
      options: { className: "toast-approved-custom" }
    }
  };

  const handleStatusSubmit = async () => {

    if (!formData) {
      toast.error("Form data is missing.", { className: "toast-error-custom" });
      return;
    }

    setShowUpdateModal(false);

    const statusValue = statusMapping[formData.candidateStatus];

    const payload = {
      status: statusValue,
      comment: formData.candidateComment,
      userId: id
    };
    try {
      const response = await dispatch(updateCandidateStatus(payload)).unwrap();

      if (response?.success === true) {
        const { msg, options } = toastConfig[formData.candidateStatus] || {};
        toast(msg, options);
        reset({ candidateComment: "" });
        setTimeout(() => {
          navigate("/admin/candidate")
        }, 2000);
      } else {
        toast.error(response?.reason || "Unexpected server response", {
          className: "toast-error-custom",
        });
      }
    } catch (err) {
      toast.error(err?.message || "Server error", { className: "toast-error-custom" });
    }
    finally {
      setFormData(null);
    }
  };

  // for getting the current date
  const getCurrentDate = () => {
    const todayDate = new Date();
    const month = String(todayDate.getMonth() + 1).padStart(2, '0'); // month is 0-indexed
    const year = todayDate.getFullYear();
    const date = String(todayDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${date}`;
  };

  const toSentenceCase = (str) => {
    if (!str) return "";
    const normalizedStr = str.toLowerCase().replaceAll(/_/g, " ");
    return normalizedStr.charAt(0).toUpperCase() + normalizedStr.slice(1);
  };

  const handleUpdateClick = (data) => {
    setShowUpdateModal(true);
    setFormData(data);
  };

  const canShowFinalUpdateCandidateSection = User?.documentUploadStatus === "SENT_FOR_APPROVAL" && ((isApprover === true) || canViewFinalCandidateStatus);
  const hideStatus = ["PENDING", "PARTIAL_COMPLETED", "SENT_FOR_APPROVAL", "APPROVED"];
  const canShowReviewSection =
    !hideStatus.includes(User?.documentUploadStatus) &&
    ((invitedBy === email) || canViewCandidateStatus);

  if (!canView) {
    return <p className='perm-msg'>You don’t have permission to view candidate details!</p>;
  }

  const permAddrData = addresses?.find(addr => addr.addressType === "PERMANENT") || addresses?.[0] || {};
  const currAddrData = addresses?.find(addr => addr.addressType === "CURRENT") || addresses?.[1] || {};
  return <>{
    status === 'loading' ?
      <p>Loading....</p> : (
        <div className='container-fluid form-navbar'>
          <div className='personaldetail-form'>
            <div className='UniversalHeadline'>
              <h5 style={{ marginBottom: '10px' }}>Candidate Name : <strong>{User.fullName}</strong>, Status:  <b>{toSentenceCase(User.documentUploadStatus)}</b></h5>
            </div>
            {/* personal detail form data */}
            <div className='personalDetailForm'>
              <div className='personalDetailHeading'> <h6 className='personalDetailHeadline'>PERSONAL DETAILS</h6> </div>
              <div className="preview-table">
                <table role="presentation" className='table table-hover table-vcenter text-nowrap mb-0'>
                  <tbody>
                    <tr>
                      <td><strong>First Name :</strong><span>{User?.firstName || ''}</span></td>
                      <td><strong>Middle Name :</strong><span>{User?.middleName || ''}</span></td>
                      <td><strong>Last Name :</strong><span>{User?.lastName || ''}</span></td>
                    </tr>
                    <tr>
                      <td><strong>Email :</strong><span>{User?.personalEmail || ''}</span></td>
                      <td><strong>Mobile Number :</strong><span>{User?.mobile || ''}</span></td>
                      <td><strong>Alternative Mob. No :</strong><span>{personalDetail?.secondaryMobile || ''}</span></td>
                    </tr>
                    <tr>
                      <td><strong>Gender :</strong><span>{personalDetail?.gender || ''}</span></td>
                      <td><strong>Photo :</strong><span>{fileTypeObject?.passportphoto ? (
                        <button type="button" className='text-info btn-interactive' onClick={() =>
                          handleViewFile('passportphoto')} target="_blank" rel="noopener noreferrer">
                          View
                        </button>
                      ) : (
                        ''
                      )}</span></td>
                      <td><strong>Mother Name :</strong><span>{personalDetail?.motherName || ''}</span></td>
                    </tr>
                    <tr>
                      <td><strong>Father Name :</strong><span>{personalDetail?.fatherName || ''}</span></td>
                      <td><strong>UAN No :</strong><span>{personalDetail?.uanNo || ''}</span></td>
                      <td><strong>Aadhar No :</strong><span>{personalDetail?.adhaarNo || ''}</span>
                        <span>{fileTypeObject?.aadhaarcard ? (
                          <button type="button" className='text-info btn-interactive' onClick={() => handleViewFile('aadhaarcard')} target="_blank" rel="noopener noreferrer">
                            View
                          </button>
                        ) : (
                          'N/A'
                        )}</span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Pan No :</strong><span>{personalDetail?.panNo || ''}</span>
                        <span>{fileTypeObject?.pancard ? (
                          <button type="button" className='text-info btn-interactive' onClick={() => handleViewFile('pancard')} target="_blank" rel="noopener noreferrer">
                            View
                          </button>
                        ) : (
                          ''
                        )}</span></td>
                      <td colSpan={2}></td>
                    </tr>

                  </tbody>
                </table>
              </div>
            </div>
            <div className='personalDetailForm'>
              <div className='personalDetailHeading'> <h6 className='personalDetailHeadline'>CONTACT DETAILS</h6> </div>
              <h6 style={{ margin: '10px 10px -7px 10px' }}>Permanent Address</h6>
              <div className="preview-table">
                <table role="presentation" className='table table-hover table-vcenter text-nowrap mb-0'>
                  <tbody>
                    <tr>
                      <td colSpan='3'><strong>Address :</strong><span>{(addresses?.length > 0) ? permAddrData?.address1 + " " + permAddrData?.address2 + " " + permAddrData?.address3 : ''}</span></td>
                    </tr>
                    <tr>
                      <td><strong>Pincode :</strong><span>{(addresses?.length > 0) ? permAddrData?.zip : ''}</span></td>
                      <td><strong>State :</strong><span>{(addresses?.length > 0) ? permAddrData?.state : ''}</span></td>
                      <td><strong>City :</strong><span>{(addresses?.length > 0) ? permAddrData?.city : ''}</span></td>
                    </tr>
                    <tr>
                      <td><strong>Nationality:</strong><span>{(addresses?.length > 0) ? permAddrData?.nationality : ''}</span></td>
                      <td><strong>Country :</strong><span>{(addresses?.length > 0) ? permAddrData?.country : ''}</span></td>
                      <td></td>
                    </tr>
                    <tr>
                      <td><strong>Emergency Contact No :</strong><span>{(addresses?.length > 0) ? permAddrData?.emergencyContactNumber : ''}</span></td>
                      <td><strong>Their name with relation :</strong><span>{(addresses?.length > 0) ? permAddrData?.emergencyContactName : ''}</span></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* If sameAsPermanentAddress is true, show heading and text inline */}
              {User.addresses?.sameAsPermanentAddress ? (
                <h6 style={{ margin: '10px 10px -2px 10px' }}>
                  Current Address : <span style={{ fontWeight: 'normal' }}>Same As Permanent Address</span>
                </h6>
              ) : (
                <>
                  <h6 style={{ margin: '10px 10px -7px 10px' }}>Current Address</h6>
                  <div className="preview-table">
                    <table role="presentation" className='table table-hover table-vcenter text-nowrap mb-0'>
                      <tbody>
                        <tr>
                          <td colSpan='3'><strong>Address :</strong>{(addresses?.length > 0) ? (<span>{currAddrData?.address1 + " " + currAddrData?.address2 + " " + currAddrData?.address3}</span>) : (<span></span>)}</td>
                        </tr>
                        <tr>
                          <td><strong>Pincode :</strong><span>{(addresses?.length > 0) ? currAddrData?.zip : ''}</span></td>
                          <td><strong>State :</strong><span>{(addresses?.length > 0) ? currAddrData?.state : ''}</span></td>
                          <td><strong>City :</strong><span>{(addresses?.length > 0) ? currAddrData?.city : ''}</span></td>
                        </tr>
                        <tr>
                          {/* <td><strong>Nationality:</strong><span>{(addresses?.length > 0) ? addresses[1]?.nationality : ''}</span></td> */}
                          <td><strong>Country :</strong><span>{(addresses?.length > 0) ? currAddrData?.country : ''}</span></td>
                          <td></td>
                        </tr>
                        <tr>
                          <td><strong>Emergency Contact No :</strong><span>{(addresses?.length > 0) ? currAddrData?.emergencyContactNumber : ''}</span></td>
                          <td><strong>Their name with relation :</strong><span>{(addresses?.length > 0) ? currAddrData?.emergencyContactName : ''}</span></td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className='personalDetailForm'>
              <div className='personalDetailHeading'> <h6 className='personalDetailHeadline'>EDUCATIONAL DETAILS</h6> </div>
              <div className="preview-table addressHistory">
                <table role="presentation" className='table table-hover table-vcenter text-nowrap mb-0'>
                  <tbody>
                    <tr>
                      <td style={{ width: '180px' }}><strong>Degree/Qualification Name</strong></td>
                      <td style={{ width: '180px' }}><strong>College / Institute Name</strong></td>
                      <td><strong>Location</strong></td>
                      {/* <td><strong>Field Of Study</strong></td> */}
                      <td style={{ width: '200px' }}><strong>Passed Year</strong></td>
                      {/* <td><strong>Roll No</strong></td> */}
                      <td><strong>Grade/Percentage</strong></td>
                      <td><strong>Attachments</strong></td>
                    </tr>
                    {educationDetails?.length > 0 ? (
                      educationDetails.map((education, index) => {
                        let uploadType = "";

                        if (index === 0) {
                          uploadType = "10thMarksheet";
                        } else if (index === 1) {
                          uploadType = "12thMarksheet";
                        } else {
                          uploadType = `${education.degreeName}Certificate`;
                        }

                        let formattedDuration = "";
                        let formattedStartDate = "N/A";
                        let formattedEndDate = "N/A";
                        let formattedYear = "N/A";

                        if (education?.fromDate && education?.toDate) {
                          const startDate = parseISO(education.fromDate);
                          const endDate = parseISO(education.toDate);

                          // Date format
                          formattedStartDate = format(startDate, "dd/MM/yyyy");
                          formattedEndDate = format(endDate, "dd/MM/yyyy");

                          // Only Year
                          formattedYear = format(endDate, "yyyy");

                          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                            const duration = intervalToDuration({
                              start: startDate,
                              end: endDate,
                            });

                            formattedDuration = `${duration.years || 0} Years, ${duration.months || 0} Months`;
                          } else {
                            formattedDuration = "Invalid Date Range";
                          }
                        } else if (education?.toDate) {
                          // If fromDate is null, get year from toDate
                          const endDate = parseISO(education.toDate);

                          if (!isNaN(endDate.getTime())) {
                            formattedEndDate = format(endDate, "dd/MM/yyyy");
                            formattedYear = format(endDate, "yyyy");
                          }
                        } else {
                          formattedDuration = "N/A";
                        }

                        console.log('fileTypeObject[uploadType]', fileTypeObject[uploadType])
                        return (
                          <tr key={education.id}>
                            <td>{education?.degreeName || ''}</td>
                            <td>{education?.institutionName || ''}</td>
                            <td>{education?.location || ''}</td>
                            <td>{formattedYear}</td>
                            <td>{education?.gradeOrcgpa || ''}</td>
                            <td><span>{fileTypeObject[uploadType.toLowerCase()] ? (
                              <button type="button" className='text-info btn-interactive' onClick={() => handleViewFile(uploadType)} target="_blank" rel="noopener noreferrer">
                                View
                              </button>
                            ) : (
                              ''
                            )}</span></td>
                          </tr>
                        )
                      })
                    ) : <tr><td colSpan='8'></td></tr>}
                  </tbody>
                </table>
              </div>

              {employmentHistory?.length > 0 ? (
                <>
                  <h6 style={{ marginTop: '10px', margin: '10px 10px 0' }}>Employment History</h6>
                  {employmentHistory?.map((employment, index) => {
                    const formatDate = (str) => str ? new Date(str).toLocaleDateString('en-GB') : "";
                    const fromDate = formatDate(employment.employmentPeriodFrom);
                    const toDate = formatDate(employment.employmentPeriodTo);
                    const empHistoryPeriodOfStay = fromDate && toDate ? `${fromDate} - ${toDate}` : "";
                    return (
                      <div className="preview-table" key={employment.id}>
                        <table role="presentation" className='table table-hover table-vcenter text-nowrap mb-0'>
                          <tbody>
                            <tr>
                              <td style={{ width: '75px', textAlign: 'center' }}><strong>{index + 1}.</strong></td>
                              <td><strong>Previous Employer Name :</strong><span>{employment?.previousEmployerName || ''}</span></td>
                              <td><strong>Employee Code :</strong><span>{employment?.employeeCode || ''}</span></td>
                              <td><strong>Office Phone No :</strong><span>{employment?.mobileNumber || ''}</span></td>
                              <td><strong>Designation :</strong><span>{employment?.designation || ''}</span></td>
                            </tr>
                            <tr>
                              <td></td>
                              <td><strong>Department :</strong><span>{employment?.department || ''}</span></td>
                              <td><strong>Reasons For Leaving :</strong><span>{employment?.reasonForLeaving || ''}</span></td>
                              <td><strong>Employment Period :</strong><span>{empHistoryPeriodOfStay || ''}</span></td>
                              <td><strong>Position :</strong><span>{employment?.positionType || ''}</span></td>
                            </tr>
                            <tr>
                              <td></td>
                              <td><strong>{"Manager's Name :"}</strong><span>{employment?.managerName || ''}</span></td>
                              <td><strong>{"Manager's Contact No :"}</strong><span>{employment?.managerContactNo || ''}</span></td>
                              <td><strong>{"Manager's Email ID :"}</strong><span>{employment?.managerEmail || ''}</span></td>
                              <td><strong>Address :</strong><span>{employment?.address || ''}</span></td>
                            </tr>
                            <tr>
                              <td></td>
                              <td><strong>Exp. / Rel. Letter :</strong><span>{fileTypeObject[(`expcertfile.${index}`)] ? (fileTypeObject[`expcertfile.${index}`] &&
                                <button type="button" className='text-info btn-interactive' onClick={() => handleViewFile(`expcertfile.${index}`)} target="_blank" rel="noopener noreferrer">
                                  View
                                </button>
                              ) : (
                                'N/A'
                              )}</span>
                                <span className="mx-1">/</span>
                                <span>{fileTypeObject[(`relievingletterfile.${index}`)] ? (fileTypeObject[`relievingletterfile.${index}`] &&
                                  <button type="button" className='text-info btn-interactive' onClick={() =>
                                    handleViewFile(`relievingletterfile.${index}`)} target="_blank" rel="noopener noreferrer">
                                    View
                                  </button>
                                ) : (
                                  'N/A'
                                )}</span> </td>
                              <td><strong>App. / Offer Letter :</strong><span>{fileTypeObject[(`offerletter.${index}`)] ? (fileTypeObject[`offerletter.${index}`] &&
                                <button type="button" className='text-info btn-interactive' onClick={() => handleViewFile(`offerletter.${index}`)} target="_blank" rel="noopener noreferrer">
                                  View
                                </button>
                              ) : (
                                'N/A'
                              )}</span></td>
                              <td><strong>Salary Slips :</strong><span>{fileTypeObject[`lastthreemonthsalaryslip1.${index}`] ? (fileTypeObject[`lastthreemonthsalaryslip1.${index}`] &&
                                <button type="button" className='text-info btn-interactive' onClick={() => handleViewFile(`lastthreemonthsalaryslip1.${index}`)} target="_blank" rel="noopener noreferrer">
                                  View
                                </button>
                              ) : (
                                'N/A'
                              )}</span>
                                <span>{fileTypeObject[`lastthreemonthsalaryslip2.${index}`] ? (fileTypeObject[`lastthreemonthsalaryslip2.${index}`] &&
                                  <button type="button" className='text-info btn-interactive' onClick={() =>
                                    handleViewFile(`lastthreemonthsalaryslip2.${index}`)
                                  } target="_blank" rel="noopener noreferrer">
                                    View
                                  </button>
                                ) : (
                                  'N/A'
                                )}</span>
                                <span>{fileTypeObject[`lastthreemonthsalaryslip3.${index}`] ? (fileTypeObject[`lastthreemonthsalaryslip3.${index}`] &&
                                  <button type="button" className='text-info btn-interactive' onClick={() => handleViewFile(`lastthreemonthsalaryslip3.${index}`)} target="_blank" rel="noopener noreferrer" >
                                    View
                                  </button>
                                ) : (
                                  'N/A'
                                )}</span>
                              </td>
                              <td><strong>Bank Statements :</strong><span>{fileTypeObject[`lastthreemonthbankstatement.${index}`] ? (fileTypeObject[`lastthreemonthbankstatement.${index}`] &&
                                <button type="button" className='text-info btn-interactive' onClick={() => handleViewFile(`lastthreemonthbankstatement.${index}`)} target="_blank" rel="noopener noreferrer">
                                  View
                                </button>
                              ) : (
                                'N/A'
                              )}</span></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>)
                  })}
                </>
              ) : (
                <h6 style={{ marginTop: '10px', margin: '10px 10px 10px' }}>Employment History :<span style={{ fontWeight: 'normal' }}> No Employment History Details</span></h6>
              )}
            </div>
            <div className='personalDetailForm'>
              <div className='personalDetailHeading'> <h6 className='personalDetailHeadline'>Additional DETAILS</h6> </div>
              {additionalDetails?.passportNumber ? (
                <>
                  <h6 style={{ marginTop: '10px', margin: '10px 10px -7px 10px' }}>Passport Details</h6>
                  <div className="preview-table">
                    <table role="presentation" className='table table-hover table-vcenter text-nowrap mb-0'>
                      <tbody>
                        <tr>
                          <td><strong>Passport No. :</strong><span>{additionalDetails?.passportNumber || ''}</span></td>
                          <td><strong>Passport Front Image :</strong><span>{fileTypeObject[`passportfrontimage`] ? (fileTypeObject[`passportfrontimage`] &&
                            <button type="button" className='text-info btn-interactive' onClick={() => handleViewFile(`passportfrontimage`)} target="_blank" rel="noopener noreferrer">
                              View
                            </button>
                          ) : (
                            'N/A'
                          )}</span></td>
                          <td><strong>Passport Last Page Image :</strong><span>{fileTypeObject[`passportlastpageimage`] ? (fileTypeObject[`passportlastpageimage`] &&
                            <button type="button" className='text-info btn-interactive' onClick={() => handleViewFile(`passportlastpageimage`)} target="_blank" rel="noopener noreferrer">
                              View
                            </button>
                          ) : (
                            'N/A'
                          )}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>) : (
                <h6 style={{ marginTop: '10px', margin: '10px 10px 10px' }}>Passport Details : <span style={{ fontWeight: 'normal' }}>--No Passport Details--</span></h6>
              )}
              {/* {professionalReferences?.length > 0 && professionalReferences[0]?.isRelative === true ? (
                <>
                  <h6 style={{ marginTop: '10px', margin: '10px 10px 0' }}>Relative Working With Moptra Infotech : <span style={{ fontWeight: 'normal' }}>Yes</span></h6>
                  <div className="preview-table addressHistory">
                    <table role="presentation" className='table table-hover table-vcenter text-nowrap mb-0'>
                      <tbody>
                        <tr>
                          <td className='serial-no-column'><strong>S.No.</strong></td>
                          <td><strong>Name</strong></td>
                          <td><strong>Relationship</strong></td>
                          <td><strong>Department</strong></td>
                          <td><strong>Designation</strong></td>
                          <td><strong>Employee ID</strong></td>
                        </tr>
                        {
                          professionalReferences.map((relativeInfo, index) => (
                            <tr key={relativeInfo.id}>
                              <td className='serial-no-column'><strong>{index + 1}.</strong></td>
                              <td>{relativeInfo?.name || ''}</td>
                              <td>{relativeInfo?.relationship || ''}</td>
                              <td>{relativeInfo?.department || ''}</td>
                              <td>{relativeInfo?.designation || ''}</td>
                              <td>{relativeInfo?.employeeId || ''}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <h6 style={{ marginTop: '10px', margin: '10px 10px 0' }}>Relative Working With Moptra Infotech : <span style={{ fontWeight: 'normal' }}>No</span></h6>
              )} */}
              {additionalDetails?.illnessDeclaration ? (
                <>
                  <h6 style={{ marginTop: '10px', margin: '10px 10px 0' }}>Details Of Any Major Illness/Surgery In The Past 3 Years : <span style={{ fontWeight: 'normal' }}>Yes</span></h6>
                  <div className="preview-table addressHistory">
                    <table role="presentation" className='table table-hover table-vcenter text-nowrap mb-0'>
                      <tbody>
                        <tr>
                          <td style={{ width: '75%' }}><strong>Details</strong></td>
                          <td><strong>Supporting Document</strong></td>
                        </tr>
                        <tr>
                          <td style={{lineHeight: '22px'}}>{additionalDetails?.illnessDeclaration || ''}</td>
                          <td><span>{fileTypeObject[`illnessdocument`] ? (fileTypeObject[`illnessdocument`] &&
                            <button type="button" className='text-info btn-interactive' onClick={() => handleViewFile(`illnessdocument`)} target="_blank" rel="noopener noreferrer">
                              View
                            </button>
                          ) : (
                            'N/A'
                          )}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <h6 style={{ marginTop: '10px', margin: '10px 10px 0' }}>Details Of Any Major Illness/Surgery In The Past 3 Years : <span style={{ fontWeight: 'normal' }}>No</span></h6>
              )}
              {additionalDetails?.hobbiesDeclaration ? (
                <>
                  <h6 style={{ marginTop: '10px', margin: '10px 10px 20px' }}>hobbies, interests and professional alignments : <span style={{ fontWeight: 'normal', textTransform: "none", lineHeight: "22px" }}>{additionalDetails?.hobbiesDeclaration}</span></h6>
                </>
              ) : (
                <h6 style={{ marginTop: '10px', margin: '10px 10px 20px' }}>hobbies, interests and professional alignments :<span style={{ fontWeight: 'normal' }}>Not mentioned</span></h6>
              )}
              <div className="personalDetailForm">
                <div className="personalDetailHeading">
                  <h6 className="personalDetailHeadline">{`Declaration `}</h6>
                </div>
                <div className="declaration-container">
                  <ul>
                    <li>
                      I am accepting the defined policies and processes in Moptra Infotech.
                    </li>
                    <li>
                      I also agree that my candidature with Moptra Infotech is valid only till I abide by the policies and processes of Moptra Infotech.
                    </li>
                    <li>
                      I give my consent and authorize Moptra Infotech (or a third party appointed by Moptra Infotech) to contact any former employee as indicated above and carry out all Background checks not restricted to education, employment, database search, address verification, criminal check, reference check deemed appropriate through this selection procedure/continued employment. I authorize my former employers, agencies educational institutes etc. to release any information pertaining to any employment/education and I release them from any liability in doing so.
                    </li>
                    <li>
                      {" "}
                      I have not been charge sheeted for any offense under the CRPC and the IPC or any other criminal Laws in India or any other country. I shall promptly notify Moptra Infotech and the relevant stakeholders, if I am being sheeted for any offense now or any time during my tenure at Moptra Infotech.
                    </li>
                    <li className="declarationCheck">
                      <input id="declareCheck"
                        type="checkbox"
                        className="lastDeclarecheck"
                        checked={(additionalDetails?.finalDeclaration) ?? (User?.documentUploadStatus !== "PENDING" && User?.documentUploadStatus !== "PARTIAL_COMPLETED")}
                        readOnly
                      />
                      <label htmlFor="declareCheck">
                        <span className="required">*</span>I hereby declare that all information that I have provided in this form are true to the best of my acknowledge, and that any misrepresentation of information by me will disqualify my candidature and subsequent employment with Moptra Infotech Pvt. Ltd.
                      </label>
                    </li>
                    {errors.declaration && (
                      <div className="declarationErrorMessage">
                        Please check the declaration to proceed further
                      </div>
                    )}
                  </ul>
                </div>
              </div>
            </div>
            {canShowFinalUpdateCandidateSection && <FinalCandidateStatus />}
            {canShowReviewSection && (
              <div>
                <h5 className='review-action'>Please provide your review and update candidate status</h5>
                <form className='comment-status-form' onSubmit={handleSubmit(handleUpdateClick)}>
                  <div className='comment-status-container'>
                    <div>
                      <label htmlFor="comment">Add Comment <span className="required">*</span></label>
                      <textarea id="comment" rows={5} placeholder='Add...' {...register("candidateComment", {
                        required: "comment is required to add",
                        minLength: {
                          value: 2,
                          message: "Must contain at least two characters",
                        },
                      })} />
                      {errors.candidateComment && (
                        <div className="error-msg">
                          {errors.candidateComment.message}
                        </div>
                      )}
                    </div>
                    <div>
                      <label htmlFor="status">Status <span className="required">*</span></label>
                      <select {...register("candidateStatus", { required: true })} id="status">
                        <option value="" hidden>select status</option>
                        {/* Fallback to "SUBMITTED" if status is null or not in our map */}
                        {(ALLOWED_TRANSITIONS[User?.documentUploadStatus] || ALLOWED_TRANSITIONS["SUBMITTED"]).map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <div>
                      <button type="submit" className="saveNextBtn">save & Notify</button>
                    </div>
                  </div>
                </form>
              </div>
            )}
            <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
              <Modal.Header>
                <Modal.Title>Confirm Update Candidate Status</Modal.Title>
                <button
                  onClick={() => setShowUpdateModal(false)}
                  className="modal-close"
                >
                  <img
                    src={CrossIcon}
                    alt="Close"
                    style={{ width: "20px", height: "20px" }}
                  />
                </button>
              </Modal.Header>
              <Modal.Body>
                Are you sure you want to update the candidate status?
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="cancel-cta"
                  style={{ color: "white" }}
                  onClick={() => setShowUpdateModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="update-cta"
                  style={{ backgroundColor: "red", color: "white" }}
                  onClick={handleStatusSubmit}
                >
                  Update
                </Button>
              </Modal.Footer>
            </Modal>

            <ToastContainer />
          </div>
        </div >
      )}
  </>
}
